import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { CrmType, CrmLabels } from '../../utils/enums';
import { CrmConnectionInfo } from '../../utils/types';

interface CrmConnectionNameModalProps {
  crmType: CrmType | null;
  connectedCrms: CrmConnectionInfo[];
  onCancel: () => void;
  onConfirm: (name: string) => void;
}

const getDefaultName = (
  crmType: CrmType,
  connectedCrms: CrmConnectionInfo[]
): string => {
  const base = `My ${CrmLabels[crmType]}`;
  const existingNames = new Set(
    connectedCrms.map((c) => c.name.trim().toLowerCase())
  );
  if (!existingNames.has(base.toLowerCase())) {
    return base;
  }
  let suffix = 2;
  while (existingNames.has(`${base} ${suffix}`.toLowerCase())) {
    suffix++;
  }
  return `${base} ${suffix}`;
};

export const CrmConnectionNameModal: React.FC<CrmConnectionNameModalProps> = ({
  crmType,
  connectedCrms,
  onCancel,
  onConfirm,
}) => {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (crmType !== null) {
      setName(getDefaultName(crmType, connectedCrms));
      setError(null);
    }
  }, [crmType]);

  const handleConfirm = () => {
    if (crmType === null) return;
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Please enter a name for this connection.');
      return;
    }
    const isDuplicate = connectedCrms.some(
      (c) => c.name.trim().toLowerCase() === trimmedName.toLowerCase()
    );
    if (isDuplicate) {
      setError('A connection with this name already exists.');
      return;
    }
    onConfirm(trimmedName);
  };

  const label = crmType !== null ? CrmLabels[crmType] : '';

  return (
    <Transition.Root show={crmType !== null} as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-[100] inset-0 overflow-y-auto"
        onClose={onCancel}
      >
        <div className="flex items-center justify-center min-h-screen p-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="relative bg-white rounded-2xl text-left shadow-2xl transform transition-all sm:my-8 sm:max-w-sm sm:w-full border border-gray-100 p-6">
              <Dialog.Title className="text-lg font-bold text-gray-900 mb-1">
                Name this {label} connection
              </Dialog.Title>
              <p className="text-sm text-gray-500 mb-4">
                Use a name that helps you tell this account apart from others
                you connect later.
              </p>
              <input
                autoFocus
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirm();
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#3B9970]"
              />
              {error && <p className="text-xs text-red-600 mt-1.5">{error}</p>}
              <div className="flex justify-end gap-2 mt-5">
                <button
                  className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800"
                  onClick={onCancel}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-[#3B9970] text-white rounded-md text-sm font-semibold hover:bg-[#2d7556]"
                  onClick={handleConfirm}
                >
                  Continue to {label}
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
